import type { AdapterType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, cva, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NoddiHoverTooltip, NoddiIconButton } from 'noddi-ui-web';
import { DEV_SETTINGS_STALE_TIME_MS, type AdapterEnv } from 'noddi-util';
import { Fragment, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isProduction, setEnvFavicon } from '../../constants/environment';
import { useAuthContext } from '../../contexts/useAuthContext';
import { useFixedBannerHeight } from '../../hooks/useFixedBannerHeight';
import { useApiBaseUrlOverride, useSetShowEnvBanner, useShowEnvBanner } from '../../stores/DevSettingsStore';
import { buildRestoreTooltip } from './buildRestoreTooltip';
import { devSettingsAdapterEnv } from './devSettingsAdapterEnv';
import { getEnvBannerVariant, type EnvBannerSegment, type EnvBannerSegmentKind } from './getEnvBannerVariant';

type AdapterBannerInfo = {
  /** Adapter provider type when brand/org has an enabled adapter. */
  adapterType?: AdapterType | string | null;
  /** Adapter API env (test|prod). Independent of deployment env. */
  adapterEnv?: AdapterEnv | string | null;
};

type Props = {
  /**
   * Optional adapter context from the host app (e.g. customer stationary
   * garages). When set, the banner appends adapter type + env so testers
   * know which provider env outbound hits. Superuser DevSettings also
   * supplies adapter env when props are omitted.
   */
  adapter?: AdapterBannerInfo | null;
  /**
   * Route of the host app's Dev settings page, which makes the banner a link
   * to the full restore provenance. Omitted by apps without that page (the
   * customer app), which then only get the hover card.
   */
  devSettingsPath?: string;
};

export const EnvBanner = ({ adapter, devSettingsPath }: Props) => {
  const rawEnv = (import.meta.env.VITE_ENVIRONMENT as string)?.toLowerCase();
  const env = rawEnv === 'local' ? 'localhost' : rawEnv;
  const showEnvBanner = useShowEnvBanner();
  const setShowEnvBanner = useSetShowEnvBanner();
  const apiOverride = useApiBaseUrlOverride();

  const { isLoggedIn, userData } = useAuthContext();
  // Endpoint is superuser-only and requests use the active (impersonated) token.
  // Gate on effective user permissions so impersonating a non-superuser skips the call.
  const isEffectiveSuperUser = Boolean(userData?.user?.isSuperuser);
  const shouldFetch = isLoggedIn && isEffectiveSuperUser && !!env && !isProduction;

  const { data: devSettings, error: _devSettingsError } = noddiAsync.useGet({
    type: URLKeys.getDevSettings,
    input: undefined,
    queryConfig: { enabled: shouldFetch, staleTime: DEV_SETTINGS_STALE_TIME_MS }
  });

  // Host can pass adapter context (customer: only when brand has adapter-enabled
  // departments). When the prop is omitted (admin/partner/facility), fall back
  // to DevSettings adapter env for superusers so the outbound target is visible.
  const adapterEnv =
    adapter === undefined ? (devSettingsAdapterEnv(devSettings) ?? null) : (adapter?.adapterEnv ?? null);
  const adapterType = adapter === undefined ? null : (adapter?.adapterType ?? null);

  const isDbProd = devSettings?.database.environment === 'prod';
  const isAdapterProd = adapterEnv === 'prod';
  const isVisible = !!env && !isProduction && showEnvBanner;
  // The strip grows when a narrow window wraps a fact onto another line.
  // The sidebar offset and the in-flow spacer both read this measured height.
  const bannerRef = useFixedBannerHeight('--env-banner-height', isVisible);

  useEffect(() => {
    if (isDbProd) {
      // Reset favicon to default (production) when connected to prod database
      setEnvFavicon(false);
    }
  }, [isDbProd]);

  if (!isVisible) {
    return null;
  }

  const restoreStamp = devSettings?.database.restoreStamp ?? null;
  const restoreStampStatus = devSettings?.database.restoreStampStatus ?? null;

  const buildTimeUrl = import.meta.env.VITE_APP_BE_URL;
  const apiUrl = apiOverride || (typeof buildTimeUrl === 'string' && buildTimeUrl.length > 0 ? buildTimeUrl : null);

  const { variant, label, segments, split } = getEnvBannerVariant({
    env,
    isAdapterProd,
    hasDevSettings: !!devSettings,
    isLoggedIn,
    apiOverride,
    apiUrl,
    databaseEnvironment: devSettings?.database.environment ?? null,
    adapterType,
    adapterEnv,
    // Undefined in Storybook and tests, which do not run the app's Vite config.
    checkout: typeof __DEV_CHECKOUT__ === 'string' ? __DEV_CHECKOUT__ : undefined,
    restoreStamp,
    restoreStampStatus
  });

  // The strip carries a rounded age. The hover card names the timestamp it was
  // rounded from, because the data, the dump file and the restore sit hours apart.
  const restore = buildRestoreTooltip({
    isDbProd,
    hasDevSettings: !!devSettings,
    restoreStamp,
    restoreStampStatus
  });

  const body: ReactNode = split ? (
    <div className='flex w-full min-w-0 items-stretch'>
      <div className={splitHalfVariants({ tone: 'localhost' })}>
        <BannerFacts segments={split.left} />
      </div>
      <div className={splitHalfVariants({ tone: 'prod' })}>
        <BannerFacts segments={split.right} />
      </div>
    </div>
  ) : (
    <BannerFacts segments={segments} />
  );

  const tooltipContent = (
    <div className='flex flex-col gap-1 text-left'>
      <p className='font-semibold'>{label}</p>
      {restore ? <p>{restore.headline}</p> : null}
      {restore && restore.rows.length > 0 ? (
        <dl className='grid grid-cols-[auto_1fr] gap-x-3'>
          {restore.rows.map(({ label: rowLabel, value }) => (
            <Fragment key={rowLabel}>
              <dt className='text-muted-foreground'>{rowLabel}</dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
      {/* Only the backoffice apps have a Dev settings page; the customer app's
          dev popover has no Database view, so it gets no pointer at all. */}
      {devSettingsPath ? (
        <p className='text-muted-foreground'>
          Click for full restore details. The X hides this banner until you turn it back on in Dev settings.
        </p>
      ) : null}
    </div>
  );

  return (
    <>
      <div ref={bannerRef} className={bannerVariants({ variant })} role='region' aria-label={label}>
        <NoddiHoverTooltip className='w-full' contentClassName='max-w-sm' content={tooltipContent}>
          {devSettingsPath ? (
            <Link to={devSettingsPath} className={BANNER_BODY_CLASS}>
              {body}
            </Link>
          ) : (
            <div className={BANNER_BODY_CLASS}>{body}</div>
          )}
        </NoddiHoverTooltip>
        <NoddiIconButton
          iconName='X'
          variant='ghost'
          size='small'
          aria-label='Hide environment banner'
          className='absolute top-1/2 right-1 z-10 -translate-y-1/2 text-current'
          onClick={() => setShowEnvBanner(false)}
        />
      </div>
      {/* Fallback until the strip has been measured. The effect then matches it exactly. */}
      <div style={{ height: 'var(--env-banner-height, 2.25rem)' }} aria-hidden />
    </>
  );
};

/** Fills the banner width, so the split halves keep their widths and the whole
 *  strip is one hover and click target rather than just the glyphs. */
const BANNER_BODY_CLASS = 'flex w-full min-w-0 items-center justify-center';

// The words repeat "local" and "prod" for different facts. The icon says which fact.
// Kinds other than `adapter` are `EnvBannerFactKind`, so a new shared fact fails this map at compile time.
const SEGMENT_ICON: Record<EnvBannerSegmentKind, NoddiIconName> = {
  'local-app': 'Monitor',
  'preview-app': 'Eye',
  database: 'Database',
  'prod-database': 'TriangleAlert',
  'unknown-database': 'CircleHelp',
  'prod-api': 'TriangleAlert',
  'api-host': 'Globe',
  'restore-copy': 'Copy',
  'restore-unknown': 'CircleQuestionMark',
  'restore-unavailable': 'Info',
  adapter: 'PlugZap'
};

const isApiSegment = (segment: EnvBannerSegment) => segment.kind === 'api-host' || segment.kind === 'prod-api';

const BannerFacts = ({ segments }: { segments: EnvBannerSegment[] }) => {
  const facts = segments.filter((segment) => !isApiSegment(segment));
  const api = segments.find(isApiSegment);

  return (
    <div className='flex w-full min-w-0 flex-col items-center justify-center gap-0.5 px-6 py-1'>
      {facts.length > 0 && <BannerSegmentRow segments={facts} />}
      {api && <BannerSegmentRow segments={[api]} subtle />}
    </div>
  );
};

const BannerSegmentRow = ({ segments, subtle }: { segments: EnvBannerSegment[]; subtle?: boolean }) => (
  <div className={cn('flex max-w-full flex-wrap items-center justify-center', subtle ? 'gap-1' : 'gap-x-3 gap-y-1')}>
    {segments.map((segment) => (
      <span
        key={segment.kind}
        className={cn('inline-flex items-center gap-1', subtle ? 'max-w-full min-w-0' : 'shrink-0')}
      >
        <NoddiIcon name={SEGMENT_ICON[segment.kind]} size={subtle ? 12 : 14} color='currentColor' />
        <span className={cn(subtle ? 'min-w-0 text-[10px] leading-none font-normal break-all' : 'leading-4')}>
          {segment.text}
        </span>
      </span>
    ))}
  </div>
);

const bannerVariants = cva('fixed inset-x-0 top-0 z-dev-banner text-center text-xs font-medium', {
  variants: {
    variant: {
      localhost: 'bg-[#17a2b8] text-white',
      dev: 'bg-[#ffc107] text-[#333]',
      staging: 'bg-[#fd7e14] text-white',
      unknownDb: 'bg-[#6c757d] text-white',
      prodDb: 'bg-[#dc3545] text-white',
      /** Adapter API targets prod (dangerous on non-prod backends). */
      adapterProd: 'bg-[#dc3545] text-white',
      /** Local DB + adapter prod: cyan left, prod red right. */
      localhostAdapterProd: 'text-white',
      apiOverride: 'bg-[#6f42c1] text-white',
      fallback: 'bg-[#ffc107] text-[#333]'
    }
  },
  defaultVariants: { variant: 'fallback' }
});

const splitHalfVariants = cva('flex w-1/2 min-w-0 items-center justify-center overflow-hidden', {
  variants: {
    tone: {
      localhost: 'bg-[#17a2b8]',
      prod: 'bg-[#dc3545]'
    }
  }
});
